import { StatsGraph } from "./stats_graph";
import { SeriesSet } from "../model/series";
import { AgeGroupData, AgeGroupStatistics } from "../model/age_group_series";
import { AgeGroupSeries } from "../model/age_group_series";
import { AgeGroupDateUtils } from "../model/age_group_date_utils";
import $ from "jquery";

class AgeGroupsView {
  private container: JQuery<HTMLElement>;
  private ageGroupNames: string[] = [
    "10代+20代",
    "30代",
    "40代",
    "50代",
    "60代〜80代",
    "男性のみ(全世代)",
  ];

  // バックエンドのグループ名とフロントエンドの表示名のマッピング
  private ageGroupNameMapping: { [key: string]: string } = {
    "10代+20代": "10代+20代",
    "30代": "30代",
    "40代": "40代",
    "50代": "50代",
    "60代〜80代": "60代〜80代",
    "男性": "男性のみ(全世代)",
  };

  constructor(container: JQuery<HTMLElement>) {
    this.container = container;
  }

  /**
   * ローディング表示
   */
  showLoading(): void {
    this.container.html(
      '<div class="indicator gr">読み込み中...</div>',
    );
  }

  /**
   * エラー表示
   */
  showError(message: string): void {
    this.container.html(
      `<div class="research_error"><p>${this.escapeHtml(message)}</p></div>`,
    );
  }

  /**
   * グラフと統計情報を描画
   */
  render(data: AgeGroupData, keyword?: string, combinationKeyword?: string): void {
    this.container.empty();

    // 1. 複数の年代のグラフが記載された1つのチャート
    this.renderCombinedChart(data);

    // 2. 各年代の個別のグラフのチャート
    this.renderIndividualCharts(data, keyword, combinationKeyword);
  }

  /**
   * 複数の年代のグラフが記載された1つのチャートを描画
   */
  private renderCombinedChart(data: AgeGroupData): void {
    const $combinedSection = $('<div class="age_groups_combined_chart"></div>');
    const $title = $('<h4 class="chart_title">世代別関心度（全体比較）</h4>');
    $combinedSection.append($title);

    // グラフ用のコンテナを作成
    const canvasId = `age_groups_combined_${Date.now()}`;
    const $canvas = $(
      `<div id="${canvasId}" class="age_groups_graph_canvas"></div>`,
    );
    $combinedSection.append($canvas);

    // SeriesSetを作成し、色情報を収集
    const seriesSet = new SeriesSet();
    const colors: string[] = [];

    // バックエンドのグループ名の順序で処理（AGE_GROUPSの順序に合わせる）
    const backendGroupNames = ["10代+20代", "30代", "40代", "50代", "60代〜80代", "男性"];

    backendGroupNames.forEach((backendGroupName) => {
      const groupData = data[backendGroupName];
      if (groupData && groupData.data && groupData.data.length > 0) {
        const series = AgeGroupSeries.convertToSeries(data, backendGroupName);
        seriesSet.add(series);
        colors.push(groupData.color);
      }
    });

    // DOMに追加してからグラフを描画
    this.container.append($combinedSection);

    // グラフを描画（DOMに追加された後に実行）
    if (seriesSet.size() > 0) {
      // DOMに追加されたことを確認するため、次のフレームで描画
      setTimeout(() => {
        const graph = new StatsGraph($canvas, {
          yAxis: StatsGraph.FloatAxis,
          legend: StatsGraph.TextLegend,
          colors: colors.length > 0 ? colors : undefined,
        });

        seriesSet.toArray().forEach((series) => {
          graph.addSeries(series);
        });

        // AgeGroupMonthlyAxisを注入
        graph.setXAxis(StatsGraph.AgeGroupMonthlyAxis);

        if (!graph.hasNoData()) {
          graph.drawAll();
        }
      }, 0);
    }
  }

  /**
   * 各年代の個別のグラフのチャートを描画
   */
  private renderIndividualCharts(data: AgeGroupData, keyword?: string, combinationKeyword?: string): void {
    const $individualSection = $(
      '<div class="age_groups_individual_charts"></div>',
    );
    const $title = $('<h4 class="chart_title">世代別関心度（個別詳細）</h4>');
    $individualSection.append($title);

    // グラフコンテナとデータを保持する配列
    const chartData: Array<{
      canvas: JQuery<HTMLElement>;
      series: any;
      color: string;
      backendGroupName: string;
    }> = [];

    // バックエンドのグループ名の順序で処理
    const backendGroupNames = ["10代+20代", "30代", "40代", "50代", "60代〜80代", "男性"];

    backendGroupNames.forEach((backendGroupName) => {
      const groupData = data[backendGroupName];
      if (groupData) {
        // フロントエンドの表示名を取得
        const displayName = this.ageGroupNameMapping[backendGroupName] || backendGroupName;
        const hasData = groupData.data && groupData.data.length > 0;

        if (hasData) {
          // データがある場合：通常のグラフを描画
          const $chartContainer = this.renderIndividualChart(
            data,
            backendGroupName,
            displayName,
            groupData.color,
            chartData,
            keyword,
            combinationKeyword,
          );
          $individualSection.append($chartContainer);
        } else {
          // データがない場合：説明文のみを表示
          const $chartContainer = this.renderEmptyDataChart(
            backendGroupName,
            displayName,
            groupData.color,
            keyword,
            combinationKeyword,
          );
          $individualSection.append($chartContainer);
        }
      }
    });

    // DOMに追加してからグラフを描画
    this.container.append($individualSection);

    // DOMに追加された後にグラフを描画
    setTimeout(() => {
      chartData.forEach(({ canvas, series, color, backendGroupName }) => {
        const graph = new StatsGraph(canvas, {
          yAxis: StatsGraph.FloatAxis,
          legend: StatsGraph.TextLegend,
          colors: [color],
        });

        graph.addSeries(series);

        // AgeGroupMonthlyAxisを注入
        graph.setXAxis(StatsGraph.AgeGroupMonthlyAxis);

        if (!graph.hasNoData()) {
          graph.drawAll();

          // 線形回帰の直線を描画
          const regressionParams = AgeGroupSeries.getLinearRegressionParams(data, backendGroupName);
          if (regressionParams) {
            const statistics = AgeGroupSeries.calculateStatistics(data, backendGroupName);
            graph.drawLinearRegressionLine(
              regressionParams.intercept,
              regressionParams.slope,
              regressionParams.startDate,
              regressionParams.endDate,
              statistics.annualSlope,
              0,
            );
          }
        }
      });

      const $annotation = $(
        '<p class="annotation">※ 直近36ヶ月のSI値を回帰分析し、30%以上の傾きを「急激に上昇(減少)」、10%〜30%未満の傾きを「上昇(減少)」、10%未満の傾きを「横ばい」と表現しています。</p>',
      );
      $individualSection.append($annotation);
    }, 0);
  }

  /**
   * データがない場合のチャートコンテナを描画（説明文のみ）
   */
  private renderEmptyDataChart(
    backendGroupName: string,
    displayName: string,
    color: string,
    keyword?: string,
    combinationKeyword?: string,
  ): JQuery<HTMLElement> {
    const $chartContainer = $(
      '<div class="age_group_individual_chart"></div>',
    );

    // タイトル
    const $chartTitle = $(`<h5 class="individual_chart_title">${displayName}</h5>`);
    $chartContainer.append($chartTitle);

    // データがない旨の説明文
    const $description = this.createEmptyDataDescription(displayName, keyword, combinationKeyword);
    $chartContainer.append($description);

    return $chartContainer;
  }

  /**
   * 個別のグラフを描画
   */
  private renderIndividualChart(
    data: AgeGroupData,
    backendGroupName: string,
    displayName: string,
    color: string,
    chartData: Array<{ canvas: JQuery<HTMLElement>; series: any; color: string; backendGroupName: string }>,
    keyword?: string,
    combinationKeyword?: string,
  ): JQuery<HTMLElement> {
    const $chartContainer = $(
      '<div class="age_group_individual_chart"></div>',
    );

    // タイトル
    const $chartTitle = $(`<h5 class="individual_chart_title">${displayName}</h5>`);
    $chartContainer.append($chartTitle);

    // 統計情報を計算
    const statistics = AgeGroupSeries.calculateStatistics(data, backendGroupName);

    // 説明文
    const $description = this.createDescription(displayName, statistics, data, backendGroupName, keyword, combinationKeyword);
    $chartContainer.append($description);

    // グラフ用のコンテナを作成
    const canvasId = `age_group_${backendGroupName.replace(/[^a-zA-Z0-9]/g, "_")}_${Date.now()}`;
    const $canvas = $(
      `<div id="${canvasId}" class="age_group_graph_canvas"></div>`,
    );
    $chartContainer.append($canvas);

    // Seriesを作成
    const series = AgeGroupSeries.convertToSeries(data, backendGroupName);

    // グラフデータを配列に追加（DOMに追加された後に描画するため）
    chartData.push({ canvas: $canvas, series, color, backendGroupName });

    return $chartContainer;
  }

  /**
   * データがない場合の説明文を作成
   */
  private createEmptyDataDescription(
    ageGroupName: string,
    keyword?: string,
    combinationKeyword?: string,
  ): JQuery<HTMLElement> {
    const $container = $('<div class="age_group_description_container"></div>');

    const $message = $(`
      <div>
        <p>
          この年代に限定しての長期的なトレンドは確認できませんでした。この場合、以下のような可能性が考えられます。
        </p>
        <ul>
          <li>年間を通じて検索ボリュームが少ない</li>
          <li>特定の時期のみ集中的に検索されて、他の時期にはほぼ検索されていない</li>
        </ul>
      </div>
    `);
    $container.append($message);

    return $container;
  }

  /**
   * 説明文を作成
   */
  private createDescription(
    ageGroupName: string,
    statistics: AgeGroupStatistics,
    data: AgeGroupData,
    backendGroupName: string,
    keyword?: string,
    combinationKeyword?: string,
  ): JQuery<HTMLElement> {
    const $container = $('<div class="age_group_description_container"></div>');

    // データ数が特定件数以下の場合はメッセージを表示し、通常の説明文は表示しない
    const groupData = data[backendGroupName];
    if (groupData && groupData.data && groupData.data.length <= 8) {
      const $warning = $(`
        <div>
        <p>
          この年代に限定しての長期的なトレンドは確認できませんでした。この場合、以下のような可能性が考えられます。
        </p>
          <ul>
            <li>年間を通じて検索ボリュームが少ない</li>
            <li>特定の時期のみ集中的に検索されて、他の時期にはほぼ検索されていない</li>
          </ul>
        </div>`
      );
      $container.append($warning);
      return $container;
    }

    // 警告がない場合のみ通常の説明文を表示
    const trendText = statistics.trend;
    const keywordText = keyword ? `「${this.escapeHtml(keyword)}」` : "";
    const combinationKeywordText = combinationKeyword ? `「${this.escapeHtml(combinationKeyword)}」` : "";

    // 先月と前年の先月のデータをチェック
    const comparisonMessage = this.getComparisonMessage(
      groupData,
      keywordText,
      combinationKeywordText,
      statistics,
    );

    const html = `
      <p class="age_group_description">
      ${comparisonMessage}<br>
        また、直近3年のトレンドを見ると <strong>${trendText}</strong>(※)。
      </p>
        `;

    $container.append($(html));

    return $container;
  }

  /**
   * 先月と前年の先月のデータをチェックして比較メッセージを生成
   */
  private getComparisonMessage(
    groupData: any,
    keywordText: string,
    combinationKeywordText: string,
    statistics: AgeGroupStatistics,
  ): string {
    if (!groupData || !groupData.data || groupData.data.length === 0) {
      return "データがありません。";
    }

    // データを日付順にソート
    const sortedData = [...groupData.data].sort(
      (a, b) =>
        new Date(a.data_date.date).getTime() -
        new Date(b.data_date.date).getTime(),
    );

    // 先月と前年の先月の情報を取得
    const { previousMonth, previousYearPreviousMonth } = AgeGroupDateUtils.getComparisonMonthInfo();

    // 先月のデータを検索
    const previousMonthData = AgeGroupDateUtils.findDataByYearMonth(
      sortedData,
      previousMonth.year,
      previousMonth.month,
    );

    // 前年の先月のデータを検索
    const previousYearPreviousMonthData = AgeGroupDateUtils.findDataByYearMonth(
      sortedData,
      previousYearPreviousMonth.year,
      previousYearPreviousMonth.month,
    );

    // ロジックに基づいてメッセージを生成
    if (previousMonthData) {
      // 先月のデータがある場合
      if (previousYearPreviousMonthData) {
        // 前年の先月のデータがある場合: 今まで通り比較する
        const increaseRateText =
          statistics.increaseRate >= 0
            ? `+ ${statistics.increaseRate.toFixed(1)} % `
            : `${statistics.increaseRate.toFixed(1)} % `;

        const monthName = AgeGroupDateUtils.getMonthOneBased(previousMonth.date);
        return `今年と昨年の${monthName}月を比べると、${keywordText ? `<strong>${keywordText}</strong>` : ""}${combinationKeywordText ? `と<strong>${combinationKeywordText}</strong>` : ""} の関心は <strong>${increaseRateText}</strong>になっています。`;
      } else {
        // 前年の先月のデータがない場合
        const monthName = AgeGroupDateUtils.getMonthOneBased(previousMonth.date);
        return `前年の${monthName}月より、${keywordText ? `<strong>${keywordText}</strong>` : ""}${combinationKeywordText ? `と<strong>${combinationKeywordText}</strong>` : ""}の関心が高いと言えます。`;
      }
    } else {
      // 先月のデータがない場合
      if (previousYearPreviousMonthData) {
        // 前年の先月のデータがある場合
        const monthName = AgeGroupDateUtils.getMonthOneBased(previousMonth.date);
        return `先月は前年の${monthName}月より、${keywordText ? `<strong>${keywordText}</strong>` : ""}${combinationKeywordText ? `と<strong>${combinationKeywordText}</strong>` : ""}の関心が低くなったと言えます。`;
      } else {
        // 前年の先月のデータがない場合
        const monthName = AgeGroupDateUtils.getMonthOneBased(previousMonth.date);
        return `先月、前年の${monthName}月のデータがありませんでした。この月の関心は薄い可能性が高いです。`;
      }
    }
  }

  /**
   * HTMLエスケープ
   */
  private escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}

export { AgeGroupsView };

